/* Athenaeum UI. MIT. Sync with node design-system/sync.mjs in athenaeum_library. */
import type { ReactNode } from "react";

/** Original MIT constellation mark, drawn from the desktop reference. */
export function AthenaeumMark() {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path
        d="m16 4 11 8-4 14H9L5 12 16 4Z"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <path
        d="m5 12 18 14M27 12 9 26M16 4v15"
        stroke="currentColor"
        strokeWidth="1"
        opacity=".45"
      />
      <circle cx="16" cy="4" r="2.5" fill="#d6b574" />
      <circle cx="5" cy="12" r="2.5" fill="currentColor" />
      <circle cx="27" cy="12" r="2.5" fill="currentColor" />
      <circle cx="9" cy="26" r="2.5" fill="currentColor" />
      <circle cx="23" cy="26" r="2.5" fill="currentColor" />
    </svg>
  );
}

export function Brand({ section }: { section?: string }) {
  return (
    <span className="ath-brand">
      <AthenaeumMark />
      <span>Athenaeum{section && <span>{section}</span>}</span>
    </span>
  );
}

export function AccountIdentity({
  username,
  detail,
}: {
  username: string;
  detail: string;
}) {
  return (
    <span className="ath-identity">
      <span className="ath-avatar" aria-hidden="true">
        {Array.from(username)[0]?.toUpperCase()}
      </span>
      <span className="ath-identity-copy">
        <strong>{username}</strong>
        <small>{detail}</small>
      </span>
    </span>
  );
}

export function PageHeading({
  title,
  description,
  actions,
  className = "",
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={`ath-page-heading ${className}`}>
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="ath-page-actions">{actions}</div>}
    </header>
  );
}

export function AuthFrame({
  section = "Library",
  title = "Your local knowledge. A shared home.",
  description = "Keep your wiki local. Share the pages you choose, with the people you choose.",
  children,
}: {
  section?: string;
  title?: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="ath-auth">
      <aside className="ath-auth-story">
        <div className="auth-product-mark">
          <AthenaeumMark />
        </div>
        <h2>{title}</h2>
        <p>{description}</p>
        <ul className="ath-auth-facts">
          <li>
            <FactIcon kind="lock" />
            Choose what leaves your computer
          </li>
          <li>
            <FactIcon kind="file" />
            Read and share plain Markdown
          </li>
          <li>
            <FactIcon kind="history" />
            Review every change together
          </li>
        </ul>
      </aside>
      <div className="ath-auth-content">{children}</div>
    </div>
  );
}

function FactIcon({ kind }: { kind: "lock" | "file" | "history" }) {
  const paths = {
    lock: "M7 10V7a5 5 0 0 1 10 0v3M5 10h14v11H5Zm7 4v3",
    file: "M5 3h9l5 5v13H5ZM14 3v6h5M8 13h8M8 17h6",
    history: "M3 11a9 9 0 1 1 2 7M3 4v7h7M12 7v6l4 2",
  };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[kind]} />
    </svg>
  );
}
